import { create } from "zustand";

interface SearchSpecificationStore {
  userId: string | null;
  currentSearchSpecificationId: string | null;
  initializeUser: (userId: string) => void;
  setCurrentSearchSpecificationId: (currentSearchSpecificationId: string | null) => void;
  clear: () => void;
}

type StoredSearchSpecificationState = {
  userId: string;
  currentSearchSpecificationId: string | null;
};

const SEARCH_SPECIFICATION_LEGACY_KEY = "currentSearchSpecificationId";
const SEARCH_SPECIFICATION_STORAGE_PREFIX = "search-specification-store-";

const getStorageName = (userId: string) => `${SEARCH_SPECIFICATION_STORAGE_PREFIX}${userId}`;

const readStoredState = (userId: string): StoredSearchSpecificationState | null => {
  try {
    const raw = localStorage.getItem(getStorageName(userId));
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<StoredSearchSpecificationState>;
    if (typeof parsed.userId !== "string" || parsed.userId !== userId) {
      return null;
    }

    return {
      userId,
      currentSearchSpecificationId:
        typeof parsed.currentSearchSpecificationId === "string" ? parsed.currentSearchSpecificationId : null,
    };
  } catch (error) {
    console.error("Failed to read stored search specification state:", error);
    return null;
  }
};

const writeStoredState = (state: StoredSearchSpecificationState): void => {
  try {
    if (!state.currentSearchSpecificationId) {
      localStorage.removeItem(getStorageName(state.userId));
      return;
    }

    localStorage.setItem(getStorageName(state.userId), JSON.stringify(state));
  } catch (error) {
    console.error("Failed to persist search specification state:", error);
  }
};

const cleanupOtherUserStores = (currentStorageName: string): void => {
  try {
    const keysToRemove: string[] = [];

    for (let index = 0; index < localStorage.length; index++) {
      const key = localStorage.key(index);
      if (key?.startsWith(SEARCH_SPECIFICATION_STORAGE_PREFIX) && key !== currentStorageName) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch (error) {
    console.error("Failed to cleanup search specification state:", error);
  }
};

const migrateLegacySearchSpecification = (userId: string): void => {
  try {
    const storageName = getStorageName(userId);
    if (localStorage.getItem(storageName)) {
      localStorage.removeItem(SEARCH_SPECIFICATION_LEGACY_KEY);
      return;
    }

    const legacySearchSpecificationId = localStorage.getItem(SEARCH_SPECIFICATION_LEGACY_KEY);
    if (!legacySearchSpecificationId) {
      return;
    }

    writeStoredState({
      userId,
      currentSearchSpecificationId: legacySearchSpecificationId,
    });
    localStorage.removeItem(SEARCH_SPECIFICATION_LEGACY_KEY);
  } catch (error) {
    console.error("Failed to migrate legacy search specification state:", error);
  }
};

export const useSearchSpecificationStore = create<SearchSpecificationStore>((set, get) => ({
  userId: null,
  currentSearchSpecificationId: null,
  initializeUser: (userId: string) => {
    const normalizedUserId = userId.trim();
    if (!normalizedUserId) {
      return;
    }

    const storageName = getStorageName(normalizedUserId);
    cleanupOtherUserStores(storageName);
    migrateLegacySearchSpecification(normalizedUserId);

    const storedState = readStoredState(normalizedUserId);
    set({
      userId: normalizedUserId,
      currentSearchSpecificationId: storedState?.currentSearchSpecificationId ?? null,
    });
  },
  setCurrentSearchSpecificationId: (currentSearchSpecificationId: string | null) => {
    const userId = get().userId;

    set({ currentSearchSpecificationId });

    if (!userId) {
      return;
    }

    writeStoredState({
      userId,
      currentSearchSpecificationId,
    });
  },
  clear: () => {
    const userId = get().userId;

    set({ currentSearchSpecificationId: null });

    if (!userId) {
      return;
    }

    writeStoredState({
      userId,
      currentSearchSpecificationId: null,
    });
  },
}));
